import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { HeartHandshake } from "lucide-react";

const GIFTS = [
  { value: "varenyky", key: "gift_pierogi", emoji: "🥟" },
  { value: "borch", key: "gift_borscht", emoji: "🍲" },
  { value: "other", key: "gift_other", emoji: "🌸" },
];

export default function SupportMe() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    gift_type: "varenyky",
    quantity: "",
    when: "",
    notes: "",
    preferred_language: lang,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/gift-offers", form);
      toast.success(t("support.success"));
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="support-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow"><HeartHandshake className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {t("support.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("support.title")}</h1>
        <p className="mt-6 text-forest-700 leading-relaxed">{t("support.lede")}</p>
      </div>

      <div className="mt-14 grid lg:grid-cols-3 gap-10">
        <aside className="card-warm lg:col-span-1">
          <p className="eyebrow">{t("support.why_title")}</p>
          <p className="mt-4 text-sm text-forest-700 leading-relaxed">{t("support.why_body")}</p>
          <p className="mt-6 text-xs text-forest-700/70 italic">{t("support.no_pressure")}</p>
          <p className="mt-4 text-[10px] uppercase tracking-[0.28em] text-wheat-700">— Isa Westervoorde</p>
        </aside>

        {done ? (
          <div className="card-warm lg:col-span-2" data-testid="support-success">
            <p className="font-serif text-3xl text-forest-900">🙏</p>
            <p className="mt-3 text-forest-700">{t("support.success")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="card-warm space-y-5 lg:col-span-2" data-testid="support-form">
            <h3 className="font-serif text-2xl text-forest-900">{t("support.form_title")}</h3>
            <div>
              <label className="field-label">{t("support.gift_type")}</label>
              <div className="grid sm:grid-cols-3 gap-3">
                {GIFTS.map((g) => (
                  <label
                    key={g.value}
                    data-testid={`gift-option-${g.value}`}
                    className={`cursor-pointer rounded-2xl border p-4 text-sm transition text-center ${
                      form.gift_type === g.value
                        ? "border-wheat-500 bg-wheat-50/60 text-forest-900"
                        : "border-forest-900/15 bg-white/60 text-forest-700 hover:border-wheat-500/50"
                    }`}
                  >
                    <input type="radio" name="gift" className="sr-only" checked={form.gift_type === g.value} onChange={() => setForm({ ...form, gift_type: g.value })} />
                    <div className="text-2xl">{g.emoji}</div>
                    <div className="mt-1 font-semibold">{t(`support.${g.key}`)}</div>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="field-label">{t("book.name")}</label>
                <input required data-testid="support-name" className="field-input" value={form.name} onChange={onChange("name")} />
              </div>
              <div>
                <label className="field-label">{t("book.email")}</label>
                <input required type="email" data-testid="support-email" className="field-input" value={form.email} onChange={onChange("email")} />
              </div>
              <div>
                <label className="field-label">{t("book.phone")}</label>
                <input data-testid="support-phone" className="field-input" value={form.phone} onChange={onChange("phone")} />
              </div>
              <div>
                <label className="field-label">{t("support.when")}</label>
                <input data-testid="support-when" className="field-input" value={form.when} onChange={onChange("when")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label">{t("support.quantity")}</label>
                <input data-testid="support-quantity" className="field-input" value={form.quantity} onChange={onChange("quantity")} />
              </div>
              <div className="sm:col-span-2">
                <label className="field-label">{t("support.notes")}</label>
                <textarea rows={4} data-testid="support-notes" className="field-input" value={form.notes} onChange={onChange("notes")} />
              </div>
            </div>
            <button disabled={submitting} className="btn-primary" data-testid="support-submit">
              {submitting ? t("common.loading") : t("support.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
